import React, { useState, useEffect, useCallback } from 'react';
import { Button, Input, Form, message } from 'antd';
import { CheckCircleFilled, SyncOutlined, CloseCircleFilled, DownOutlined, UpOutlined } from '@ant-design/icons';
import { verifyEhrConnection, createEhrConnection } from 'api/ehr';
import { TEhrVerifyType, TEhrStepsConfig, TEhrExternalInfoField } from 'types/ehrConnection';
import EhrStepGuide from './EhrStepGuide';
import s from './s.module.less';

type Props = {
    ehrName: string;
    verifyType: TEhrVerifyType;
    stepsConfig?: TEhrStepsConfig;
    agentEmail: string;
    externalInfoFields?: TEhrExternalInfoField[];
    onSuccess: () => void;
    onBack: () => void;
};

type VerifyState = 'idle' | 'verifying' | 'success' | 'failed';

const VerifyStep = ({ ehrName, verifyType, stepsConfig, agentEmail, externalInfoFields, onSuccess, onBack }: Props) => {
    const [state, setState] = useState<VerifyState>('idle');
    const [showSteps, setShowSteps] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [form] = Form.useForm();

    useEffect(() => {
        if (state === 'success') {
            const timer = setTimeout(onSuccess, 2000);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [state, onSuccess]);

    const doVerify = useCallback(async () => {
        setState('verifying');
        setError(null);
        try {
            const res = await verifyEhrConnection({ ehrSystem: ehrName });
            if (res && !res.error) {
                const data = res.data?.data;
                if (data?.verified || data?.connectionStatus === 'connected') {
                    setState('success');
                } else {
                    setState('failed');
                    setError(data?.error || 'Connection could not be verified. Please check the setup steps and try again.');
                }
            } else {
                setState('failed');
                setError('Verification request failed. Please try again.');
            }
        } catch {
            setState('failed');
            setError('An unexpected error occurred. Please try again.');
        }
    }, [ehrName]);

    useEffect(() => {
        if (verifyType === 'agent_auto') {
            doVerify();
        }
    }, [verifyType, doVerify]);

    const handlePasswordSubmit = async () => {
        try {
            const values = await form.validateFields();
            setState('verifying');
            setError(null);

            const payload: { ehrSystem: string; password: string; email?: string; externalInfo?: Record<string, string> } = {
                ehrSystem: ehrName,
                password: values.password,
            };

            if (values.email) {
                payload.email = values.email;
            }

            if (externalInfoFields?.length) {
                const ext: Record<string, string> = {};
                externalInfoFields.forEach((f) => {
                    if (values[f.key]) ext[f.key] = values[f.key];
                });
                if (Object.keys(ext).length) payload.externalInfo = ext;
            }

            const createRes = await createEhrConnection(payload);
            if (!createRes || createRes.error) {
                setState('failed');
                setError(createRes?.error || 'Failed to create connection. Please check your credentials and try again.');
                return;
            }

            const verifyRes = await verifyEhrConnection({ ehrSystem: ehrName });
            if (verifyRes && !verifyRes.error) {
                const data = verifyRes.data?.data;
                if (data?.verified || data?.connectionStatus === 'connected') {
                    setState('success');
                } else {
                    setState('failed');
                    setError(data?.error || 'Connection could not be verified. Please check your credentials and try again.');
                }
            } else {
                setState('failed');
                setError('Verification request failed. Please try again.');
            }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            if (e?.errorFields) return;
            setState('failed');
            setError('An unexpected error occurred.');
        }
    };

    if (state === 'success') {
        return (
            <div className={s.verifySuccess}>
                <div className={s.verifySuccessIcon}>
                    <CheckCircleFilled />
                </div>
                <div>
                    <div className={s.verifySuccessTitle}>{ehrName} connected successfully!</div>
                    <div className={s.verifySuccessDesc}>
                        Klarity now has access to your {ehrName} account and can sync your data.
                    </div>
                </div>
            </div>
        );
    }

    if (verifyType === 'agent_auto') {
        return (
            <>
                {state === 'verifying' && (
                    <div className={s.verifyCard}>
                        <div className={s.verifyIconBlue}>
                            <SyncOutlined spin style={{ fontSize: 28 }} />
                        </div>
                        <div className={s.verifyTitle}>Verifying connection to {ehrName}...</div>
                        <div className={s.verifySub}>This usually takes a few seconds</div>
                        <div className={s.verifyProgress}>
                            <div className={s.verifyProgressBar} />
                        </div>
                    </div>
                )}
                {state === 'failed' && (
                    <div className={s.verifyFail}>
                        <div className={s.verifyFailIcon}>
                            <CloseCircleFilled />
                        </div>
                        <div className={s.verifyFailTitle}>Connection verification failed</div>
                        <div className={s.verifyFailDesc}>{error}</div>
                        <div className={s.verifyEmailHelper}>
                            <div className={s.verifyEmailHelperLabel}>Make sure you invited this email:</div>
                            <div className={s.verifyEmailRow}>
                                <div className={s.verifyEmailDisplay}>{agentEmail}</div>
                                <Button
                                    icon={<SyncOutlined />}
                                    onClick={() => {
                                        navigator.clipboard.writeText(agentEmail);
                                        message.success('Email copied');
                                    }}
                                >
                                    Copy
                                </Button>
                            </div>
                        </div>
                        <span className={s.verifyShowSteps} onClick={() => setShowSteps(!showSteps)}>
                            {showSteps ? 'Hide' : 'Show'} setup steps
                            {showSteps ? <UpOutlined /> : <DownOutlined />}
                        </span>
                        {showSteps && stepsConfig && (
                            <div style={{ marginTop: 16, textAlign: 'left' }}>
                                <EhrStepGuide config={stepsConfig} agentEmail={agentEmail} />
                            </div>
                        )}
                        <div className={s.verifyActions} style={{ marginTop: 24 }}>
                            <Button onClick={onBack}>Go back</Button>
                            <Button type="primary" onClick={doVerify}>
                                Retry verification
                            </Button>
                        </div>
                    </div>
                )}
            </>
        );
    }

    if (verifyType === 'agent_password' || verifyType === 'account_password') {
        const isAccountMode = verifyType === 'account_password';
        return (
            <div className={s.verifyCard}>
                {state === 'verifying' ? (
                    <>
                        <div className={s.verifyIconBlue}>
                            <SyncOutlined spin style={{ fontSize: 28 }} />
                        </div>
                        <div className={s.verifyTitle}>Connecting to {ehrName}...</div>
                        <div className={s.verifySub}>Verifying your credentials</div>
                        <div className={s.verifyProgress}>
                            <div className={s.verifyProgressBar} />
                        </div>
                    </>
                ) : (
                    <>
                        <div className={s.verifyTitle}>
                            {isAccountMode
                                ? `Enter your ${ehrName} login credentials`
                                : `Enter the password you created for the agent account`}
                        </div>
                        <div className={s.verifySub}>
                            {isAccountMode
                                ? 'We need your account credentials to connect to your EHR.'
                                : 'The password you set when creating the agent user in your EHR.'}
                        </div>
                        {state === 'failed' && error && (
                            <div className={s.verifyError}>{error}</div>
                        )}
                        <Form form={form} layout="vertical" className={s.verifyFormWrap}>
                            {isAccountMode && (
                                <Form.Item
                                    name="email"
                                    label={<span className={s.verifyFormLabel}>Email</span>}
                                    rules={[{ required: true, message: 'Email is required' }]}
                                >
                                    <Input size="large" placeholder="your-email@example.com" />
                                </Form.Item>
                            )}
                            <Form.Item
                                name="password"
                                label={<span className={s.verifyFormLabel}>Password</span>}
                                rules={[{ required: true, message: 'Password is required' }]}
                            >
                                <Input.Password size="large" placeholder="Enter password" />
                            </Form.Item>
                            {externalInfoFields?.map((field) => (
                                <Form.Item
                                    key={field.key}
                                    name={field.key}
                                    label={<span className={s.verifyFormLabel}>{field.label}</span>}
                                    rules={field.required ? [{ required: true, message: `${field.label} is required` }] : undefined}
                                >
                                    <Input size="large" placeholder={field.placeholder} type={field.type} />
                                </Form.Item>
                            ))}
                        </Form>
                        <div className={s.verifyActions}>
                            <Button onClick={onBack}>Go back</Button>
                            <Button type="primary" onClick={handlePasswordSubmit}>
                                Connect
                            </Button>
                        </div>
                    </>
                )}
            </div>
        );
    }

    return null;
};

export default VerifyStep;
